import { useTranslation } from "react-i18next";
import { AppLayout, LoadingPage, Media, PageSidebar } from "shared/components";
import { Flex } from "styled-system/jsx";
import {
  OrganizationProfileTabs,
  OrganizationProfileTabsEnum,
} from "./components";
import { useState } from "react";
import {
  BackOffice,
  Profile,
  Staff,
  useOrganizationProfileForm,
} from "./views";
import { Settings } from "iconoir-react";
import { useParams } from "react-router-dom";
import {
  EnumEntityReferenceType,
  useOrganizationByIdQuery,
} from "gql/generated";
import { FormProvider } from "react-hook-form";
import { ButtonProps } from "@ttc3k/trekker";
import { scale } from "@cloudinary/url-gen/actions/resize";
import { css } from "styled-system/css";
import { isLandscape } from "shared/utils/isLandscape";

export const OrganizationProfile = () => {
  const { t } = useTranslation("directory", { keyPrefix: "ORGANIZATION" });
  const { organizationId } = useParams();

  const [tab, setTab] = useState<OrganizationProfileTabsEnum>(
    OrganizationProfileTabsEnum.BackOffice,
  );
  const [saveButtonProps, setSaveButtonProps] = useState<ButtonProps | null>(
    null,
  );

  const { data, loading, error } = useOrganizationByIdQuery({
    variables: { id: organizationId ?? "" },
  });

  const { form: orgProfileForm, onSubmit: onSubmitProfileForm } =
    useOrganizationProfileForm({ organization: data?.organizationById });
  const profileFormId = "organization-profile-form";

  const getCurrentOrganizationProfileTab = (
    tab: OrganizationProfileTabsEnum,
  ) => {
    switch (tab) {
      case OrganizationProfileTabsEnum.Profile:
        return (
          <FormProvider {...orgProfileForm}>
            <form onSubmit={onSubmitProfileForm} id={profileFormId}>
              <Profile />
            </form>
          </FormProvider>
        );
      case OrganizationProfileTabsEnum.Media:
        return (
          <Media
            _entityId={organizationId ?? ""}
            entityType={EnumEntityReferenceType.Organization}
            images={data?.organizationById?.images ?? []}
            setSaveButtonProps={setSaveButtonProps}
            disableFeaturedImageTag
          />
        );
      case OrganizationProfileTabsEnum.BackOffice:
        return <BackOffice organization={data?.organizationById} />;
      case OrganizationProfileTabsEnum.Staff:
        return <Staff />;
    }
  };

  if (loading) return <LoadingPage />;

  if (error && !loading) {
    throw new Error(error.message);
  }

  if (!data || !data.organizationById) {
    throw new Error("Cannot find organization");
  }

  const buttonDisabled =
    tab === OrganizationProfileTabsEnum.Profile
      ? !orgProfileForm.formState.isDirty
      : undefined;
  const buttonDisplay = [
    OrganizationProfileTabsEnum.Profile,
    OrganizationProfileTabsEnum.Media,
  ].includes(tab)
    ? "block"
    : "none";
  const buttonFormId =
    tab === OrganizationProfileTabsEnum.Profile ? profileFormId : undefined;

  return (
    <>
      <AppLayout
        sidebar={
          <PageSidebar
            title={t("HEADER.TITLE")}
            Icon={Settings}
            description={t("HEADER.DESCRIPTION")}
            imageProps={{
              image: data?.organizationById?.logo,
              imageStyleProps: {
                objectFit: "scale-down",
                className: css({
                  h: "260px",
                  w: "260px",
                }),
              },

              resize: isLandscape(data.organizationById.logo)
                ? scale().width(260)
                : scale().height(260),
            }}
            primaryButtonProps={{
              disabled: buttonDisabled,
              form: buttonFormId,
              display: buttonDisplay,
              children: t("HEADER.CTA_BUTTON_COPY"),
              visual: "accent.blue",
              ...(saveButtonProps ? { ...saveButtonProps } : {}),
            }}
          />
        }
        content={
          <>
            <Flex flexDir={"column"} h={"fit-content"} w={"full"} gap={"400"}>
              <OrganizationProfileTabs
                currentTab={tab}
                onTabChange={(newTab) => setTab(newTab)}
              />
              {getCurrentOrganizationProfileTab(tab)}
            </Flex>
          </>
        }
      />
    </>
  );
};
